import {
  STUDIO_FEATURED_OUTCOME_SOURCE,
  STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID,
} from '@/features/productization/candidate-lifecycle-entry-evidence'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

export const LANDING_STUDIO_HANDOFF_KEY = 'cogo:landing-studio-handoff:v1'
export const SHOWROOM_STUDIO_HANDOFF_VERSION =
  'cogo.showroom-studio-handoff.v2' as const

const MAX_STORED_HANDOFF_LENGTH = 8_000
const MAX_PROMPT_LENGTH = 2_000
const MAX_NOTICE_PROMPT_LENGTH = 140

export interface LandingStudioHandoff {
  source: 'cogo-xyz-landing'
  prompt: string
  createdAt: string
}

export const SHOWROOM_SELECTED_TASKS = {
  customer: [
    'join-flow',
    'member-only-content',
    'welcome-journey',
  ],
  operator: [
    'membership-activity',
    'access-roles',
    'change-impact',
  ],
} as const

export const SHOWROOM_TRIAL_STAGES = [
  'ready',
  'checkout',
  'member',
  'article',
  'welcome',
] as const

export type ShowroomPreviewMode = keyof typeof SHOWROOM_SELECTED_TASKS
export type ShowroomSelectedTask =
  (typeof SHOWROOM_SELECTED_TASKS)[ShowroomPreviewMode][number]
export type ShowroomTrialStage = (typeof SHOWROOM_TRIAL_STAGES)[number]
export type ShowroomEntryAction = 'guided-customization' | 'connect-subscriber-list'

export type ShowroomProductionEvidence =
  | false
  | { publicationReleaseId: string }

export interface ShowroomTrialSummary {
  scope: 'session-only'
  stage: ShowroomTrialStage
  checkoutCompleted: boolean
  memberAccess: boolean
  articleOpened: boolean
  welcomeDeliveryRecorded: boolean
  productionEvidence: ShowroomProductionEvidence
}

export interface ShowroomStudioHandoff {
  schemaVersion: typeof SHOWROOM_STUDIO_HANDOFF_VERSION
  source: typeof STUDIO_FEATURED_OUTCOME_SOURCE
  skuId: typeof STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID
  mode: ShowroomPreviewMode
  selectedTask: ShowroomSelectedTask | null
  entryAction: ShowroomEntryAction
  trialSummary: ShowroomTrialSummary
  createdAt: string
}

function sessionStorageOrNull(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  return (
    Object.keys(value).sort().join('\0') === [...expected].sort().join('\0')
  )
}

function isCanonicalTimestamp(value: unknown): value is string {
  if (typeof value !== 'string' || value.length !== 24) return false
  const timestamp = new Date(value)
  return !Number.isNaN(timestamp.getTime()) && timestamp.toISOString() === value
}

function normalizePrompt(prompt: string): string {
  return prompt.trim().slice(0, MAX_PROMPT_LENGTH)
}

function compactPrompt(prompt: string): string {
  return prompt.replace(/\s+/g, ' ').trim()
}

function parseStoredJson(raw: string | null): Record<string, unknown> | null {
  if (!raw || raw.length > MAX_STORED_HANDOFF_LENGTH) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

function parseLandingHandoff(raw: string | null): LandingStudioHandoff | null {
  const parsed = parseStoredJson(raw)
  if (
    !parsed ||
    !hasExactKeys(parsed, ['source', 'prompt', 'createdAt']) ||
    parsed.source !== 'cogo-xyz-landing' ||
    typeof parsed.prompt !== 'string' ||
    !isCanonicalTimestamp(parsed.createdAt)
  ) {
    return null
  }
  const prompt = normalizePrompt(parsed.prompt)
  if (!prompt) return null
  return {
    source: 'cogo-xyz-landing',
    prompt,
    createdAt: parsed.createdAt,
  }
}

function isPreviewMode(value: unknown): value is ShowroomPreviewMode {
  return value === 'customer' || value === 'operator'
}

function isSelectedTaskForMode(
  value: unknown,
  mode: ShowroomPreviewMode,
): value is ShowroomSelectedTask | null {
  return (
    value === null ||
    (typeof value === 'string' &&
      SHOWROOM_SELECTED_TASKS[mode].some((task) => task === value))
  )
}

function isEntryAction(value: unknown): value is ShowroomEntryAction {
  return value === 'guided-customization' || value === 'connect-subscriber-list'
}

function isTrialStage(value: unknown): value is ShowroomTrialStage {
  return (
    typeof value === 'string' &&
    SHOWROOM_TRIAL_STAGES.some((stage) => stage === value)
  )
}

function parseProductionEvidence(
  value: unknown,
): ShowroomProductionEvidence | null {
  if (value === false) return false
  if (
    isRecord(value) &&
    hasExactKeys(value, ['publicationReleaseId']) &&
    typeof value.publicationReleaseId === 'string' &&
    value.publicationReleaseId.length > 0
  ) {
    return { publicationReleaseId: value.publicationReleaseId }
  }
  return null
}

function parseTrialSummary(value: unknown): ShowroomTrialSummary | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      'scope',
      'stage',
      'checkoutCompleted',
      'memberAccess',
      'articleOpened',
      'welcomeDeliveryRecorded',
      'productionEvidence',
    ]) ||
    value.scope !== 'session-only' ||
    !isTrialStage(value.stage) ||
    typeof value.checkoutCompleted !== 'boolean' ||
    typeof value.memberAccess !== 'boolean' ||
    typeof value.articleOpened !== 'boolean' ||
    typeof value.welcomeDeliveryRecorded !== 'boolean'
  ) {
    return null
  }

  const productionEvidence = parseProductionEvidence(value.productionEvidence)
  if (productionEvidence === null) return null

  const stageIndex = SHOWROOM_TRIAL_STAGES.indexOf(value.stage)
  if (
    value.checkoutCompleted !== (stageIndex >= 2) ||
    value.memberAccess !== (stageIndex >= 2) ||
    value.articleOpened !== (stageIndex >= 3) ||
    value.welcomeDeliveryRecorded !== (stageIndex >= 4)
  ) {
    return null
  }

  return {
    scope: 'session-only',
    stage: value.stage,
    checkoutCompleted: value.checkoutCompleted,
    memberAccess: value.memberAccess,
    articleOpened: value.articleOpened,
    welcomeDeliveryRecorded: value.welcomeDeliveryRecorded,
    productionEvidence,
  }
}

function parseShowroomHandoff(raw: string | null): ShowroomStudioHandoff | null {
  const parsed = parseStoredJson(raw)
  if (
    !parsed ||
    !hasExactKeys(parsed, [
      'schemaVersion',
      'source',
      'skuId',
      'mode',
      'selectedTask',
      'entryAction',
      'trialSummary',
      'createdAt',
    ]) ||
    parsed.schemaVersion !== SHOWROOM_STUDIO_HANDOFF_VERSION ||
    parsed.source !== STUDIO_FEATURED_OUTCOME_SOURCE ||
    parsed.skuId !== STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID ||
    !isPreviewMode(parsed.mode) ||
    !isSelectedTaskForMode(parsed.selectedTask, parsed.mode) ||
    !isEntryAction(parsed.entryAction) ||
    !isCanonicalTimestamp(parsed.createdAt)
  ) {
    return null
  }
  const trialSummary = parseTrialSummary(parsed.trialSummary)
  if (!trialSummary) return null

  return {
    schemaVersion: SHOWROOM_STUDIO_HANDOFF_VERSION,
    source: STUDIO_FEATURED_OUTCOME_SOURCE,
    skuId: STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID,
    mode: parsed.mode,
    selectedTask: parsed.selectedTask,
    entryAction: parsed.entryAction,
    trialSummary,
    createdAt: parsed.createdAt,
  }
}

export function saveLandingStudioHandoff(prompt: string): LandingStudioHandoff | null {
  const storage = sessionStorageOrNull()
  if (!storage) return null
  const normalized = normalizePrompt(prompt)
  if (!normalized) return null
  const handoff: LandingStudioHandoff = {
    source: 'cogo-xyz-landing',
    prompt: normalized,
    createdAt: new Date().toISOString(),
  }
  try {
    storage.setItem(LANDING_STUDIO_HANDOFF_KEY, JSON.stringify(handoff))
    return handoff
  } catch {
    return null
  }
}

export function saveShowroomStudioHandoff(input: {
  mode: ShowroomPreviewMode
  selectedTask: ShowroomSelectedTask | null
  entryAction: ShowroomEntryAction
  trialSummary: ShowroomTrialSummary
}): ShowroomStudioHandoff | null {
  const storage = sessionStorageOrNull()
  if (!storage) return null
  const candidate = {
    schemaVersion: SHOWROOM_STUDIO_HANDOFF_VERSION,
    source: STUDIO_FEATURED_OUTCOME_SOURCE,
    skuId: STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID,
    mode: input.mode,
    selectedTask: input.selectedTask,
    entryAction: input.entryAction,
    trialSummary: input.trialSummary,
    createdAt: new Date().toISOString(),
  }
  const serialized = JSON.stringify(candidate)
  const handoff = parseShowroomHandoff(serialized)
  if (!handoff) return null
  try {
    storage.setItem(LANDING_STUDIO_HANDOFF_KEY, serialized)
    return handoff
  } catch {
    return null
  }
}

export function readLandingStudioHandoff(): LandingStudioHandoff | null {
  const storage = sessionStorageOrNull()
  if (!storage) return null
  try {
    return parseLandingHandoff(storage.getItem(LANDING_STUDIO_HANDOFF_KEY))
  } catch {
    return null
  }
}

export function readShowroomStudioHandoff(): ShowroomStudioHandoff | null {
  const storage = sessionStorageOrNull()
  if (!storage) return null
  try {
    return parseShowroomHandoff(storage.getItem(LANDING_STUDIO_HANDOFF_KEY))
  } catch {
    return null
  }
}

function isSameProductionEvidence(
  left: ShowroomProductionEvidence,
  right: ShowroomProductionEvidence,
): boolean {
  if (left === false || right === false) return left === right
  return left.publicationReleaseId === right.publicationReleaseId
}

function isSameShowroomHandoff(
  left: ShowroomStudioHandoff,
  right: ShowroomStudioHandoff,
): boolean {
  return (
    left.schemaVersion === right.schemaVersion &&
    left.source === right.source &&
    left.skuId === right.skuId &&
    left.mode === right.mode &&
    left.selectedTask === right.selectedTask &&
    left.entryAction === right.entryAction &&
    left.createdAt === right.createdAt &&
    left.trialSummary.scope === right.trialSummary.scope &&
    left.trialSummary.stage === right.trialSummary.stage &&
    left.trialSummary.checkoutCompleted ===
      right.trialSummary.checkoutCompleted &&
    left.trialSummary.memberAccess === right.trialSummary.memberAccess &&
    left.trialSummary.articleOpened === right.trialSummary.articleOpened &&
    left.trialSummary.welcomeDeliveryRecorded ===
      right.trialSummary.welcomeDeliveryRecorded &&
    isSameProductionEvidence(
      left.trialSummary.productionEvidence,
      right.trialSummary.productionEvidence,
    )
  )
}

export function removeShowroomStudioHandoff(
  expected: ShowroomStudioHandoff,
): boolean {
  const storage = sessionStorageOrNull()
  if (!storage) return false
  try {
    const current = parseShowroomHandoff(
      storage.getItem(LANDING_STUDIO_HANDOFF_KEY),
    )
    if (!current || !isSameShowroomHandoff(current, expected)) return false
    storage.removeItem(LANDING_STUDIO_HANDOFF_KEY)
    return storage.getItem(LANDING_STUDIO_HANDOFF_KEY) === null
  } catch {
    return false
  }
}

export function consumeGenericLandingStudioHandoff(): LandingStudioHandoff | null {
  const storage = sessionStorageOrNull()
  if (!storage) return null
  try {
    const handoff = parseLandingHandoff(
      storage.getItem(LANDING_STUDIO_HANDOFF_KEY),
    )
    if (!handoff) return null
    storage.removeItem(LANDING_STUDIO_HANDOFF_KEY)
    return handoff
  } catch {
    return null
  }
}

export function consumeLandingStudioHandoff(): LandingStudioHandoff | null {
  return consumeGenericLandingStudioHandoff()
}

export function consumeShowroomStudioHandoff(): ShowroomStudioHandoff | null {
  const handoff = readShowroomStudioHandoff()
  return handoff && removeShowroomStudioHandoff(handoff) ? handoff : null
}

export function buildLandingStudioNotice(handoff: LandingStudioHandoff): string {
  const compact = compactPrompt(handoff.prompt)
  const preview = compact.length > MAX_NOTICE_PROMPT_LENGTH
    ? `${compact.slice(0, MAX_NOTICE_PROMPT_LENGTH - 1)}...`
    : compact
  return `Started from coogo on cogo.xyz: "${preview}" is ready in chat. Review the first Preview before anything launches.`
}

type ShowroomStudioPromptCopy = {
  customize: string
  previewMode: Record<ShowroomPreviewMode, string>
  selectedTask: Record<ShowroomSelectedTask, string>
  trialStage: Record<ShowroomTrialStage, string | null>
  trialBoundary: string
  connectSubscribers: string
  askFirst: string
}

const SHOWROOM_STUDIO_PROMPT_COPY: Record<LocaleCode, ShowroomStudioPromptCopy> = {
  en: {
    customize: 'I want to customize the paid newsletter membership I tried in Showroom.',
    previewMode: {
      customer: 'Start with the member experience.',
      operator: 'Start with the operator experience.',
    },
    selectedTask: {
      'join-flow': 'Focus first on the signup flow.',
      'member-only-content': 'Focus first on member-only content.',
      'welcome-journey': 'Focus first on the welcome journey.',
      'membership-activity': 'Focus first on membership activity.',
      'access-roles': 'Focus first on access roles.',
      'change-impact': 'Focus first on the impact of changes.',
    },
    trialStage: {
      ready: null,
      checkout: 'I opened the sandbox checkout step.',
      member: 'I completed the sandbox signup and saw member access.',
      article: 'I also opened the member-only article.',
      welcome: 'I followed the sandbox journey through its welcome delivery record.',
    },
    trialBoundary:
      'Treat this trial as a temporary Showroom session only; it is not evidence of production activity.',
    connectSubscribers:
      'Start by connecting my existing subscriber CSV or Notion list.',
    askFirst:
      'Before proposing the first change, ask only what is necessary, at most three questions.',
  },
  ko: {
    customize: 'Showroom에서 써 본 유료 뉴스레터 멤버십을 내 것으로 바꾸고 싶어요.',
    previewMode: {
      customer: '멤버가 보는 경험부터 시작해 주세요.',
      operator: '운영자가 보는 경험부터 시작해 주세요.',
    },
    selectedTask: {
      'join-flow': '가입 흐름을 먼저 맞춰 주세요.',
      'member-only-content': '멤버 전용 콘텐츠를 먼저 맞춰 주세요.',
      'welcome-journey': '환영 여정을 먼저 맞춰 주세요.',
      'membership-activity': '멤버십 활동 현황을 먼저 맞춰 주세요.',
      'access-roles': '접근 권한을 먼저 맞춰 주세요.',
      'change-impact': '변경 영향 범위를 먼저 보여 주세요.',
    },
    trialStage: {
      ready: null,
      checkout: '샌드박스 결제 단계까지 열어 봤어요.',
      member: '샌드박스 가입을 마치고 멤버 권한까지 확인했어요.',
      article: '멤버 전용 글도 열어 봤어요.',
      welcome: '환영 발송 기록까지 전체 샌드박스 여정을 따라가 봤어요.',
    },
    trialBoundary:
      '이 체험은 임시 Showroom 세션 안에서만 진행된 것이며, 실제 운영 활동의 증거로 사용하지 마세요.',
    connectSubscribers:
      '기존 구독자 CSV 또는 Notion 목록 연결부터 시작하세요.',
    askFirst: '첫 변경 계획 전에는 꼭 필요한 것만 최대 3개 물어봐 주세요.',
  },
  ja: {
    customize: 'Showroom で試した有料ニュースレターのメンバーシップを自分向けに変更したいです。',
    previewMode: {
      customer: 'メンバー向けの体験から始めてください。',
      operator: '運営者向けの体験から始めてください。',
    },
    selectedTask: {
      'join-flow': '登録フローを先に整えてください。',
      'member-only-content': 'メンバー限定コンテンツを先に整えてください。',
      'welcome-journey': 'ウェルカムジャーニーを先に整えてください。',
      'membership-activity': 'メンバーシップの利用状況を先に整えてください。',
      'access-roles': 'アクセス権限を先に整えてください。',
      'change-impact': '変更の影響範囲を先に見せてください。',
    },
    trialStage: {
      ready: null,
      checkout: 'サンドボックスの決済手順まで開きました。',
      member: 'サンドボックス登録を完了し、メンバー権限まで確認しました。',
      article: 'メンバー限定記事も開きました。',
      welcome: 'ウェルカム配信記録までサンドボックスの流れを確認しました。',
    },
    trialBoundary:
      'この体験は一時的な Showroom セッション内だけのものであり、本番運用の実績とは扱わないでください。',
    connectSubscribers:
      '既存の購読者 CSV または Notion リストの接続から始めてください。',
    askFirst: '最初の変更案を出す前に、必要なことだけを最大3つ質問してください。',
  },
  zh: {
    customize: '我想把在 Showroom 体验过的付费通讯会员产品改成自己的版本。',
    previewMode: {
      customer: '请先从会员体验开始。',
      operator: '请先从运营体验开始。',
    },
    selectedTask: {
      'join-flow': '请先调整注册流程。',
      'member-only-content': '请先调整会员专属内容。',
      'welcome-journey': '请先调整欢迎旅程。',
      'membership-activity': '请先调整会员活动概览。',
      'access-roles': '请先调整访问权限。',
      'change-impact': '请先展示变更的影响范围。',
    },
    trialStage: {
      ready: null,
      checkout: '我已经打开了沙盒结账步骤。',
      member: '我已完成沙盒注册并确认了会员权限。',
      article: '我也打开了会员专属文章。',
      welcome: '我已体验到欢迎发送记录。',
    },
    trialBoundary: '这次体验只发生在临时 Showroom 会话中，请勿将其视为生产环境活动的证据。',
    connectSubscribers: '请先连接现有订阅者 CSV 或 Notion 列表。',
    askFirst: '提出首个变更方案前，只询问必要内容，最多三个问题。',
  },
  'zh-TW': {
    customize: '我想把在 Showroom 體驗過的付費電子報會員產品改成自己的版本。',
    previewMode: {
      customer: '請先從會員體驗開始。',
      operator: '請先從營運體驗開始。',
    },
    selectedTask: {
      'join-flow': '請先調整註冊流程。',
      'member-only-content': '請先調整會員專屬內容。',
      'welcome-journey': '請先調整歡迎旅程。',
      'membership-activity': '請先調整會員活動概覽。',
      'access-roles': '請先調整存取權限。',
      'change-impact': '請先顯示變更的影響範圍。',
    },
    trialStage: {
      ready: null,
      checkout: '我已經開啟沙盒結帳步驟。',
      member: '我已完成沙盒註冊並確認會員權限。',
      article: '我也開啟了會員專屬文章。',
      welcome: '我已體驗到歡迎發送記錄。',
    },
    trialBoundary: '這次體驗只發生在暫時的 Showroom 工作階段中，請勿將其視為正式環境活動的證據。',
    connectSubscribers: '請先連接現有訂閱者 CSV 或 Notion 清單。',
    askFirst: '提出第一個變更方案前，只詢問必要內容，最多三個問題。',
  },
}

export function buildShowroomStudioPrompt(
  handoff: ShowroomStudioHandoff,
  locale: LocaleCode = 'en',
): string {
  const copy = SHOWROOM_STUDIO_PROMPT_COPY[locale]
  return [
    copy.customize,
    copy.previewMode[handoff.mode],
    handoff.selectedTask
    ? copy.selectedTask[handoff.selectedTask] : null,
    copy.trialStage[handoff.trialSummary.stage],
    copy.trialBoundary,
    handoff.entryAction === 'connect-subscriber-list'
      ? copy.connectSubscribers
      : null,
    copy.askFirst,
  ]
    .filter((part): part is string => part !== null)
    .join(' ')
}
